<script setup>
import { providePageGuide } from "@/composables/usePageGuide"
providePageGuide()

import PageGuide from "@/components/panel/PageGuide.vue"
import BackButton from "@/components/app/BackButton/index.vue"
import "@styles/admin-ios-controls.css"
import { normalizeAuthList } from "@/constants/businessAccess"

const route = useRoute()
useHead({ bodyAttrs: { class: "support-panel-page" } })
const isHome = computed(() => route.path === "/support")
const roles = computed(() => normalizeAuthList(useAuthStore().authStore?.role ?? useCookie("role").value))
const allNavigation = [
  { title: "میزکار", to: "/support", icon: "tabler-layout-dashboard" },
  { title: "مجموعه‌ها", to: "/support/companies", icon: "tabler-building-store" },
  { title: "محتوا", to: "/support/content", icon: "tabler-message-check" },
  { title: "رسانه‌ها", to: "/support/media", icon: "tabler-photo-check" },
  { title: "دسته‌بندی‌ها", to: "/support/categories", icon: "tabler-category" },
  { title: "خدمات پیش‌فرض", to: "/support/default-services", icon: "tabler-list-details" },
  { title: "تقویم", to: "/support/calendar", icon: "tabler-calendar-cog" },
  { title: "گزارش‌های سایت", to: "/support/reports", icon: "tabler-chart-dots-3", siteManager: true },
]
const navigation = computed(() => allNavigation.filter(item => item.siteManager ? roles.value.includes("SITE_MANAGER") : roles.value.includes("SERVICE_DESK")))
const isActive = item => item.to === "/support" ? route.path === item.to : route.path.startsWith(item.to)
</script>

<template>
  <div
    class="support-layout"
    dir="rtl"
  >
    <aside class="support-sidebar">
      <NuxtLink
        class="support-brand"
        to="/support"
      >
        <span><VIcon
          icon="tabler-shield-check"
          size="25"
        /></span><div><b>الومنشی</b><small>مرکز کنترل و پشتیبانی</small></div>
      </NuxtLink>
      <nav>
        <NuxtLink
          v-for="item in navigation"
          :key="item.to"
          :to="item.to"
          :class="{ active: isActive(item) }"
        >
          <VIcon
            :icon="item.icon"
            size="20"
          /><span>{{ item.title }}</span><VIcon
            class="nav-arrow"
            icon="tabler-chevron-left"
            size="16"
          />
        </NuxtLink>
      </nav>
      <NuxtLink
        class="customer-app-link"
        to="/client-page"
      >
        <VIcon
          icon="tabler-device-mobile"
          size="20"
        /><div><b>اپ مشتری</b><small>بازگشت به نمای عمومی</small></div><VIcon
          icon="tabler-arrow-up-left"
          size="17"
        />
      </NuxtLink>
    </aside>
    <main>
      <header>
        <div class="header-title">
          <BackButton v-if="!isHome" to="/support" /><div><small>مرکز عملیات و نظارت</small><h1>{{ allNavigation.find(item => isActive(item) && item.to !== "/support")?.title || "پنل پشتیبانی" }}<PageGuide :title="allNavigation.find(item => isActive(item) && item.to !== '/support')?.title || 'پنل پشتیبانی'" /></h1></div>
        </div><div class="header-actions">
          <NuxtLink
            class="mobile-client-return"
            to="/client-page"
          >
            <VIcon
              icon="tabler-device-mobile"
              size="18"
            />
            <span>اپ مشتری</span>
            <VIcon
              icon="tabler-arrow-up-left"
              size="15"
            />
          </NuxtLink>
          <span class="desk-status"><i />سامانه فعال</span>
        </div>
      </header>
      <div class="mobile-navigation">
        <NuxtLink
          v-for="item in navigation"
          :key="item.to"
          :to="item.to"
          :class="{ active: isActive(item) }"
        >
          <VIcon
            :icon="item.icon"
            size="18"
          />{{ item.title }}
        </NuxtLink>
      </div>
      <slot />
    </main>
  </div>
</template>

<style scoped>
.support-layout,.support-layout :deep(*){font-family:"IranSans",Tahoma,Arial,sans-serif!important}.support-layout{display:grid;grid-template-columns:258px minmax(0,1fr);min-height:100vh;color:#342e45;background:#f7f7fb}.support-sidebar{position:sticky;top:0;display:flex;height:100vh;flex-direction:column;padding:22px 17px;border-left:1px solid #e7e5ee;background:rgba(255,255,255,.94);backdrop-filter:blur(18px)}.support-brand{display:flex;align-items:center;gap:11px;padding:4px 7px 24px;color:#322b42}.support-brand>span{display:grid;width:44px;height:44px;place-items:center;border-radius:14px;color:#fff;background:linear-gradient(135deg,#5944cf,#836fe2);box-shadow:0 10px 24px rgba(89,68,207,.24)}.support-brand b,.support-brand small{display:block}.support-brand b{font-size:16px}.support-brand small{margin-top:3px;color:#9891a2;font-size:9px}.support-sidebar nav{display:grid;gap:5px}.support-sidebar nav a{display:grid;grid-template-columns:25px 1fr auto;align-items:center;gap:9px;padding:11px 12px;border-radius:12px;color:#756e80;font-size:11px;font-weight:700;transition:.2s}.support-sidebar nav a:hover{color:#5745bd;background:#f5f2ff}.support-sidebar nav a.active{color:#fff;background:linear-gradient(125deg,#5945c9,#7662da);box-shadow:0 9px 22px rgba(89,69,201,.2)}.nav-arrow{opacity:.45}.customer-app-link{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:9px;margin-top:auto;padding:13px;border:1px solid #e8e4ef;border-radius:14px;color:#5d5568;background:#faf9fc}.customer-app-link b,.customer-app-link small{display:block}.customer-app-link b{font-size:11px}.customer-app-link small{margin-top:3px;color:#a099aa;font-size:8px}.support-layout>main{min-width:0;padding:25px clamp(18px,3.6vw,48px) 60px}.support-layout header,.header-title,.header-actions,.back-menu{display:flex;align-items:center}.support-layout header{justify-content:space-between;gap:18px;margin:0 auto 25px;max-width:1250px}.header-title,.header-actions{gap:12px}.back-menu{justify-content:center;width:42px;height:42px;border:1px solid #e5e0ee;border-radius:13px;color:#5d48d7;background:#fff;box-shadow:0 5px 18px rgba(47,38,76,.07)}.support-layout header small{color:#8e8798;font-size:9px}.support-layout h1{margin:3px 0 0;font-size:21px}.desk-status{display:flex;align-items:center;gap:6px;color:#777080;font-size:9px}.desk-status i{width:7px;height:7px;border-radius:50%;background:#35b581;box-shadow:0 0 0 4px rgba(53,181,129,.12)}.mobile-client-return{display:none;align-items:center;gap:6px;padding:9px 11px;border:1px solid #dcd5f5;border-radius:11px;color:#5945c9;background:#f2efff;font-size:10px;font-weight:800;white-space:nowrap}.mobile-navigation{display:none}.support-layout main>section{max-width:1250px;margin-inline:auto}@media(max-width:1000px){.support-layout{display:block}.support-sidebar{display:none}.mobile-client-return{display:flex}.mobile-navigation{display:flex;overflow:auto;gap:6px;margin:-8px 0 20px;padding:4px 0 9px;scrollbar-width:none}.mobile-navigation a{display:flex;flex:0 0 auto;align-items:center;gap:6px;padding:9px 11px;border:1px solid #e7e3ed;border-radius:11px;color:#746d7f;background:#fff;font-size:10px}.mobile-navigation a.active{border-color:#6854d1;color:#fff;background:#6854d1}}@media(max-width:600px){.support-layout>main{padding:16px 10px 40px}.support-layout header{align-items:flex-start;margin-bottom:20px}.header-actions{align-self:center}.desk-status{display:none}.support-layout h1{font-size:19px}}
</style>
